import { NIGHTMARES } from '@somnhold/content';
import { idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, SendaNpc, SlopeRules, SlopeState } from '../types.ts';
import { damageEnemy, damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { attackHero, stepToward } from './enemies.ts';

/**
 * The icy climb (docs/sendas/guerrero.md, chapter 3, act 1, «La cuesta helada»; the trail in battle/fort-layout.ts).
 * Kel leads the mule up the trail, Wenn on it, stopping while a nightmare is on either of them, while the hero lags
 * behind, or where a rock's shadow lies ahead. Rocks come down (their shadow on the snow `warn` before) on the way
 * ahead of the mule or on the hero, striking all they land by. Wolves come down the trail's edges. At the landing below
 * the door Kel plants his torch in the snow and the train waits: the peat troll at the door comes down at them, and it
 * mends itself fast unless it has burnt lately (NIGHTMARES.troll.regen): standing on the torch is burning. Dalmo keeps
 * to the mule with his pitchfork and strikes what comes at it (not what comes at Kel). The troll down, the train goes
 * on up: the mule at the door wins it. The people's blows are the common rule's (battle/senda.ts).
 *
 * Cost: three people, a handful of rocks, one troll looked at once a tick; the halt asks each nightmare once.
 */

const fresh = (): SlopeState => ({ at: 1, rocks: [], next: 0, torch: false, troll: 0, awake: false, low: 10000, swing: 0, halted: false });

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

/** A nightmare on Kel or the mule (the troll still waiting at the door doesn't count). */
const threatened = (s: BattleState, r: SlopeRules, w: SlopeState, a: SendaNpc, b: SendaNpc | undefined): boolean => {
  for (const e of s.enemies) {
    if (e.hp <= 0 || (e.id === w.troll && !w.awake)) continue;
    if (withinRange(e.x, e.y, a.x, a.y, r.halt) || (b && withinRange(e.x, e.y, b.x, b.y, r.halt))) return true;
  }
  return false;
};

/** A rock's shadow on the next bit of the way. */
const shadowAhead = (r: SlopeRules, w: SlopeState, k: SendaNpc): boolean => {
  const p = r.way[w.at];
  if (!p) return false;
  for (const x of w.rocks) if (withinRange(x.x, x.y, p.x, p.y, r.r + 300) || withinRange(x.x, x.y, k.x, k.y, r.r + 200)) return true;
  return false;
};

/** Where the next rock comes down: on the way a few rows ahead of the mule, or on the hero (from the fight's own luck). */
const aimRock = (s: BattleState, r: SlopeRules, w: SlopeState): { x: number; y: number } => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  const ahead = r.way[imin(r.way.length - 1, w.at + rngRange(s.rng, 0, 3))] as { x: number; y: number };
  const on = h && rngInt(s.rng, 10) < 4 ? { x: h.x, y: h.y } : ahead;
  return { x: on.x + rngRange(s.rng, -600, 600), y: on.y + rngRange(s.rng, -400, 400) };
};

const rockTick = (s: BattleState, r: SlopeRules, w: SlopeState): void => {
  if (!w.next) w.next = s.tick + r.first;
  if (s.tick >= w.next) {
    const p = aimRock(s, r, w);
    w.rocks.push({ x: p.x, y: p.y, t: s.tick + r.warn });
    w.next = s.tick + rngRange(s.rng, r.gap[0], r.gap[1]);
    ev(s, { t: 'senda', kind: 'rockWarn', npc: '', x: p.x, y: p.y, n: r.warn });
  }
  if (!w.rocks.length || w.rocks[0]!.t > s.tick) return;
  const keep: SlopeState['rocks'] = [];
  for (const x of w.rocks) {
    if (x.t > s.tick) {
      keep.push(x);
      continue;
    }
    ev(s, { t: 'senda', kind: 'rock', npc: '', x: x.x, y: x.y });
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, x.x, x.y, r.r)) damageHero(s, h, mulBp(h.stats.maxHp, r.heroBp), null, 'physical', true);
    for (const n of s.senda?.npcs ?? []) {
      if (n.hp <= 0 || !withinRange(n.x, n.y, x.x, x.y, r.r)) continue;
      n.hp = imax(0, n.hp - r.blows);
      ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
    }
    // (The troll shrugs them off: too big.)
    for (const e of s.enemies) if (e.hp > 0 && e.id !== w.troll && withinRange(e.x, e.y, x.x, x.y, r.r)) damageEnemy(s, e, mulBp(e.maxHp, r.foeBp), { pid: '', ignoresArmor: true });
  }
  w.rocks = keep;
};

/** Dalmo by the mule: what comes at it, he goes at, and strikes every so often; else back at its side. */
const dalmoTick = (s: BattleState, r: SlopeRules, w: SlopeState, mule: SendaNpc | undefined): void => {
  const d = npcOf(s, 'dalmo');
  if (!d || !mule) return;
  d.moving = false;
  let best: Enemy | null = null;
  let bd = r.dalmo.reach + r.dalmo.guard;
  for (const e of s.enemies) {
    if (e.hp <= 0 || (e.id === w.troll && !w.awake)) continue;
    if (!withinRange(e.x, e.y, mule.x, mule.y, r.dalmo.guard)) continue;
    const dd = Math.abs(e.x - d.x) + Math.abs(e.y - d.y);
    if (dd < bd) {
      bd = dd;
      best = e;
    }
  }
  if (best) {
    if (!withinRange(best.x, best.y, d.x, d.y, r.dalmo.reach)) {
      d.moving = true;
      stepToward(d, best.x, best.y, 70);
    } else if (s.tick >= w.swing && d.flinch <= 0) {
      w.swing = s.tick + r.dalmo.every;
      damageEnemy(s, best, r.dalmo.hit, { pid: '' });
      ev(s, { t: 'senda', kind: 'work', npc: 'dalmo', x: best.x, y: best.y });
    }
    if (d.flinch > 0) d.flinch--;
    return;
  }
  if (d.flinch > 0) d.flinch--;
  const sx = mule.x + 700;
  const sy = mule.y + 300;
  if (!withinRange(d.x, d.y, sx, sy, 250)) {
    d.moving = true;
    stepToward(d, sx, sy, 60);
  }
};

/** The peat troll: waits at the door until the mule is near, then comes down; on the torch it burns (and won't mend). */
const trollTick = (s: BattleState, r: SlopeRules, w: SlopeState, cue: (c: string) => void): void => {
  if (!w.troll) {
    const e = spawnEnemy(s, 'troll', 1, false, 0, r.door.x, r.door.y - 300, r.trollHp);
    e.facing = 256;
    e.damage = mulBp(e.damage, r.trollDmgBp);
    w.troll = e.id;
  }
  const e = findEnemy(s, w.troll);
  if (!e || e.hp <= 0) return;
  // It comes down when the torch goes in at the landing, or when the hero walks up to it.
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!w.awake && (w.torch || (h && withinRange(h.x, h.y, e.x, e.y, r.wake)) || e.hp < e.maxHp)) {
    w.awake = true;
    ev(s, { t: 'senda', kind: 'wake', npc: '', x: e.x, y: e.y });
    cue('troll');
  }
  if (w.torch && withinRange(e.x, e.y, r.torch.x, r.torch.y, r.burn)) {
    if (s.tick - (e.scorch ?? -100000) > 6) ev(s, { t: 'senda', kind: 'burn', npc: '', x: e.x, y: e.y });
    e.scorch = s.tick;
  }
  const k = idiv(e.hp * 10000, imax(1, e.maxHp));
  // Hurt and mending on its own, far from the fire: Wenn says so (once).
  const burning = s.tick - (e.scorch ?? -100000) < (NIGHTMARES.troll.regen?.burnTicks ?? 0);
  if (k > w.low + 150 && !burning && w.low < 8500) cue('mend');
  if (k < w.low) w.low = k;
};

/**
 * The troll's turn (true: nothing else for it): waiting at the door, or come down, after the hero (the one hitting it;
 * Kel and the mule only with the hero down).
 */
export const slopeHold = (s: BattleState, e: Enemy, pace: (e: Enemy) => number): boolean => {
  const w = s.senda?.slope;
  if (!s.rules?.senda?.slope || !w || e.id !== w.troll) return false;
  if (!w.awake) {
    e.anim = 'idle';
    return true;
  }
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h) return false;
  // (It moves before the nightmares' loop counts its reload and its stun down: it does both itself.)
  if (e.stun > 0) {
    e.stun--;
    e.anim = 'stun';
    return true;
  }
  if (e.cd > 0) e.cd--;
  e.anim = 'walk';
  if (withinRange(h.x, h.y, e.x, e.y, NIGHTMARES.troll.reach + 120)) attackHero(s, e, h);
  else stepToward(e, h.x, h.y, pace(e));
  return true;
};

export const slopeTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.slope;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.slope ??= fresh());
  const kel = npcOf(s, 'kel');
  const mule = npcOf(s, 'mule');
  if (!s.started) return;
  trollTick(s, r, w, cue);
  rockTick(s, r, w);
  dalmoTick(s, r, w, mule);
  if (!kel) return;
  const hero = s.heroes.find((h) => h.downTicks === 0);
  const troll = w.troll ? findEnemy(s, w.troll) : null;
  const trollUp = !!troll && troll.hp > 0;
  const stop = (w.at > r.landing && trollUp) || threatened(s, r, w, kel, mule) || !hero || !withinRange(hero.x, hero.y, kel.x, kel.y, r.wait) || shadowAhead(r, w, kel);
  w.halted = stop;
  kel.moving = false;
  if (!stop && kel.flinch <= 0) {
    const p = r.way[w.at];
    if (p) {
      kel.moving = true;
      if (stepToward(kel, p.x, p.y, r.pace) || withinRange(kel.x, kel.y, p.x, p.y, 60)) w.at = imin(r.way.length, w.at + 1);
    }
  }
  if (kel.flinch > 0) kel.flinch--;
  // The landing: the torch goes into the snow, and they wait there while the troll stands.
  if (w.at > r.landing && !w.torch) {
    w.torch = true;
    ev(s, { t: 'senda', kind: 'torch', npc: 'kel', x: r.torch.x, y: r.torch.y });
    cue('torch');
    // Up here, by the troll's door, no more wolves come down (the packs keep off it).
    s.wave.next = s.wave.spawns.length;
  }
  // The mule a hex behind Kel, on the way.
  if (mule) {
    mule.moving = false;
    if (!withinRange(mule.x, mule.y, kel.x, kel.y, 950) && mule.flinch <= 0) {
      mule.moving = true;
      stepToward(mule, kel.x, kel.y, r.pace + 6);
    }
    if (mule.flinch > 0) mule.flinch--;
  }
};

/** How far up the train is (rows of the way). */
export const slopeGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.slope;
  const w = s.senda?.slope;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: imin(r.way.length - 1, imax(0, w.at - 1)), need: r.way.length - 1 };
};

/** Won with the mule at the door. */
export const slopeResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.senda?.slope;
  const mule = npcOf(s, 'mule');
  return r && mule && withinRange(mule.x, mule.y, r.door.x, r.door.y, r.doorReach) ? 'won' : null;
};
